// Environment-aware Printify client.
//
// On non-shadow deployments (app.intrlock.io / admin.intrlock.io) we call the
// legacy pass-through Lambda at api.intrlock.io/printify/* (violates the
// connector-api-only standing rule, but retained for backward compat until CES
// cutover).
//
// On shadow.* deployments we route through the connector-api Printify adapter
// via POST /connector/action — this is the standing-rule-compliant path
// (SM-backed creds, DELETE gated, JWT-authenticated).
import { apiOrigin } from '../utils/surface';

const IS_SHADOW =
  typeof window !== 'undefined' && window.location.hostname.startsWith('shadow.');

const PRINTIFY_PROXY = `${apiOrigin()}/printify`;
const CONNECTOR_ACTION = IS_SHADOW
  ? 'https://shadow.api.intrlock.io/connector/action'
  : `${apiOrigin()}/connector/action`;
const SHOP_ID = '26546134';

// Map REST path + method → connector-api Printify adapter action name.
// Kept in one place so callers below don't repeat the mapping. Extend as new
// SPA calls are added.
type ConnectorInvoke = { action: string; params: Record<string, unknown> };
const toConnector = (path: string, options: RequestInit): ConnectorInvoke => {
  const method = (options.method || 'GET').toUpperCase();
  const body = options.body ? JSON.parse(options.body as string) : undefined;
  // /catalog/blueprints.json
  if (path === '/catalog/blueprints.json') return { action: 'list_blueprints', params: {} };
  let m;
  if ((m = path.match(/^\/catalog\/blueprints\/(\d+)\.json$/)))
    return { action: 'get_blueprint', params: { blueprint_id: m[1] } };
  if ((m = path.match(/^\/catalog\/blueprints\/(\d+)\/print_providers\.json$/)))
    return { action: 'list_blueprint_print_providers', params: { blueprint_id: m[1] } };
  if ((m = path.match(/^\/catalog\/blueprints\/(\d+)\/print_providers\/(\d+)\/variants\.json$/)))
    return { action: 'list_variants', params: { blueprint_id: m[1], print_provider_id: m[2] } };
  if ((m = path.match(/^\/shops\/([^/]+)\/products\.json$/)))
    return method === 'POST'
      ? { action: 'create_product', params: { shop_id: m[1], body } }
      : { action: 'list_products', params: { shop_id: m[1] } };
  if ((m = path.match(/^\/shops\/([^/]+)\/products\/([^/]+)\/publish\.json$/)))
    return { action: 'publish_product', params: { shop_id: m[1], product_id: m[2], body } };
  if ((m = path.match(/^\/shops\/([^/]+)\/orders\.json$/)))
    return method === 'POST'
      ? { action: 'submit_order', params: { shop_id: m[1], body } }
      : { action: 'list_orders', params: { shop_id: m[1] } };
  if ((m = path.match(/^\/shops\/([^/]+)\/orders\/shipping\.json$/)))
    return { action: 'calculate_order_shipping', params: { shop_id: m[1], body } };
  if (path === '/uploads/images.json')
    return { action: 'upload_image', params: { body } };
  throw new Error(`printifyApi: no connector-api mapping for ${method} ${path}`);
};

const printifyFetch = async (path: string, options: RequestInit = {}) => {
  if (IS_SHADOW) {
    // Canonical JWT key across the app is `intrlock_token` (set by AuthContext on login).
    // Fall back to `intrlock_jwt` in case a legacy build stored it under the old name.
    const token = typeof window !== 'undefined'
      ? (window.localStorage.getItem('intrlock_token') || window.localStorage.getItem('intrlock_jwt'))
      : null;
    const { action, params } = toConnector(path, options);
    const res = await fetch(CONNECTOR_ACTION, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ integration_key: 'printify', action, params }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: res.statusText }));
      throw new Error((err as any).error || (err as any).message || res.statusText);
    }
    const wrapped = await res.json();
    // connector-api returns { integration_key, action, data, ... } — unwrap for SPA parity
    return (wrapped && typeof wrapped === 'object' && 'data' in wrapped) ? (wrapped as any).data : wrapped;
  }
  const res = await fetch(`${PRINTIFY_PROXY}${path}`, {
    ...options,
    headers: { 'Content-Type': 'application/json', ...options.headers },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error((err as any).error || res.statusText);
  }
  return res.json();
};

// Catalog
export const getBlueprints = () => printifyFetch('/catalog/blueprints.json');
export const getBlueprintById = (id: number) => printifyFetch(`/catalog/blueprints/${id}.json`);
export const getBlueprintProviders = (id: number) => printifyFetch(`/catalog/blueprints/${id}/print_providers.json`);
export const getProviderVariants = (blueprintId: number, providerId: number) =>
  printifyFetch(`/catalog/blueprints/${blueprintId}/print_providers/${providerId}/variants.json`);

// Products
export const createProduct = (data: any) =>
  printifyFetch(`/shops/${SHOP_ID}/products.json`, { method: 'POST', body: JSON.stringify(data) });
export const getProducts = () => printifyFetch(`/shops/${SHOP_ID}/products.json`);

// Orders
export const createOrder = (data: any) =>
  printifyFetch(`/shops/${SHOP_ID}/orders.json`, { method: 'POST', body: JSON.stringify(data) });
export const getOrders = () => printifyFetch(`/shops/${SHOP_ID}/orders.json`);

// Uploads
export const uploadImage = (data: { file_name: string; contents: string }) =>
  printifyFetch('/uploads/images.json', { method: 'POST', body: JSON.stringify(data) });

// Shipping
export const getShippingEstimate = (data: {
  line_items: Array<{ product_id: string; variant_id: number; quantity: number }>;
  address_to: { first_name: string; last_name: string; address1: string; city: string; region: string; zip: string; country: string };
}) => printifyFetch(`/shops/${SHOP_ID}/orders/shipping.json`, { method: 'POST', body: JSON.stringify(data) });

// Print areas for a blueprint/provider
export const getPrintAreas = (blueprintId: number, providerId: number) =>
  printifyFetch(`/catalog/blueprints/${blueprintId}/print_providers/${providerId}/variants.json`);

// Publish product to shop
export const publishProduct = (productId: string) =>
  printifyFetch(`/shops/${SHOP_ID}/products/${productId}/publish.json`, {
    method: 'POST',
    body: JSON.stringify({
      title: true, description: true, images: true, variants: true, tags: true,
    }),
  });
